import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'

export default function ExperiencePage() {
  const [experiences, setExperiences] = useState([])
  const [loading, setLoading] = useState(true)
  const [expandedIds, setExpandedIds] = useState({})

  useEffect(() => {
    async function fetchExperiences() {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/v2/content/erfaring?sort=sortering&order=asc')
        if (res.ok) {
          const data = await res.json()
          setExperiences(data.items || [])
          // Default all expanded for optimal readability
          const initialMap = {}
          ;(data.items || []).forEach((item) => {
            initialMap[item.id] = true
          })
          setExpandedIds(initialMap)
        }
      } catch (err) {
        console.error('Failed to load experiences', err)
      } finally {
        setLoading(false)
      }
    }
    fetchExperiences()
  }, [])

  const toggleExpand = (id) => {
    setExpandedIds((prev) => ({
      ...prev,
      [id]: !prev[id],
    }))
  }

  return (
    <>
      <SEO
        title="Erfaring"
        description="Birgitte Vilsgaards erhvervserfaring og track record inden for IT & Tech recruitment, search og executive recruitment."
      />

      {/* Header Banner */}
      <section className="bg-gradient-to-b from-slate-100 to-white py-16 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-black text-black tracking-tight">
              Birgitte Vilsgaards Erfaring
            </h1>
            <p className="text-lg text-black mt-3 leading-relaxed">
              Mange års solid baggrund med IT-rekruttering, executive search og kandidatvurdering for førende danske tech-huse og IT-organisationer.
            </p>
          </div>
        </div>
      </section>

      {/* Timeline / Card Layout */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8 pb-4 border-b border-slate-100">
            <h2 className="text-2xl font-black text-black flex items-center gap-2">
              <i className="fa-solid fa-timeline text-black" aria-hidden="true" />
              <span>Erhvervserfaring</span>
            </h2>
            <button
              onClick={() => {
                const allOpen = Object.values(expandedIds).every(Boolean)
                const nextState = {}
                experiences.forEach((exp) => {
                  nextState[exp.id] = !allOpen
                })
                setExpandedIds(nextState)
              }}
              className="text-xs font-bold text-black underline cursor-pointer"
            >
              {Object.values(expandedIds).every(Boolean) ? 'Fold alle sammen' : 'Fold alle ud'}
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <div className="w-8 h-8 border-4 border-black border-t-transparent animate-spin" />
            </div>
          ) : experiences.length === 0 ? (
            <p className="text-black text-center py-12">Ingen erfaringer fundet.</p>
          ) : (
            <div className="relative border-l-2 border-black/20 pl-6 sm:pl-8 space-y-8 ml-2 sm:ml-4">
              {experiences.map((exp) => {
                const isOpen = expandedIds[exp.id]
                return (
                  <div key={exp.id} className="relative group">
                    {/* Timeline Node Icon */}
                    <div className="absolute -left-[31px] sm:-left-[39px] top-1.5 w-6 h-6 bg-white border-4 border-black" />

                    {/* Expandable Card */}
                    <div className="bg-slate-50 hover:bg-white border border-slate-200 transition-all overflow-hidden">
                      {/* Card Header (Clickable toggle) */}
                      <button
                        onClick={() => toggleExpand(exp.id)}
                        className="w-full text-left p-6 flex items-start justify-between gap-4 cursor-pointer focus:outline-none"
                      >
                        <div className="space-y-1">
                          <span className="inline-block px-3 py-1 bg-slate-200 text-black text-xs font-extrabold">
                            {exp.periode}
                          </span>
                          <h3 className="text-xl font-extrabold text-black">
                            {exp.titel}
                          </h3>
                          <p className="text-sm font-semibold text-black flex items-center gap-2">
                            <i className="fa-solid fa-building text-black" aria-hidden="true" />
                            <span>{exp.virksomhed}</span>
                          </p>
                        </div>

                        <div className="w-9 h-9 bg-white border border-slate-300 text-black flex items-center justify-center shrink-0">
                          <i
                            className={`fa-solid fa-chevron-down text-black transition-transform duration-200 ${
                              isOpen ? 'rotate-180' : ''
                            }`}
                            aria-hidden="true"
                          />
                        </div>
                      </button>

                      {/* Expandable Description */}
                      {isOpen && (
                        <div className="px-6 pb-6 pt-2 border-t border-slate-200 animate-in fade-in slide-in-from-top-1 duration-150">
                          <p className="text-black text-sm leading-relaxed whitespace-pre-line">
                            {exp.beskrivelse}
                          </p>

                          <div className="mt-4 pt-3 border-t border-slate-200 flex items-center gap-4 text-xs font-medium text-black">
                            <span className="flex items-center gap-1.5">
                              <i className="fa-solid fa-check text-black" aria-hidden="true" />
                              <span>Verificeret IT Recruitment Erfaring</span>
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* CTA Box */}
          <div className="mt-16 p-8 bg-slate-900 text-white flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl font-extrabold text-white">Vil du udnytte denne erfaring i din virksomhed?</h3>
              <p className="text-slate-300 text-sm mt-1">
                Book en samtale om dine kommende IT-ansættelser med Birgitte.
              </p>
            </div>
            <Link
              to="/kontakt"
              className="px-6 py-3.5 bg-white text-black font-bold whitespace-nowrap transition-colors shrink-0 hover:bg-slate-100"
            >
              Book en samtale
            </Link>
          </div>
        </div>
      </section>
    </>
  )
}
